<template>
  <v-dialog
    v-model="dialog"
    :width="getDialogWidth"
    :fullscreen="$vuetify.display.smAndDown"
    persistent
  >
    <template v-slot:activator="{ props }">
      <slot
        :name="'reschedule'"
        :props="props"
        :emitAction="handleSlotAction"
      >
        <v-btn
          v-bind="props"
          class="next-btn ml-2 my-2"
          @click="dialog = true"
        >
          {{ $t('content-creator.schedule-email.newsletter-button') }}
        </v-btn>
      </slot>
    </template>

    <v-stepper
      v-if="dialog && localContent"
      v-model="step"
      alt-labels
      flat
      color="primary"
      elevation="0"
    >
      <v-stepper-window
        v-model="step"
        class="pa-0 ma-0"
      >
        <v-stepper-window-item
          :value="0"
          class="py-0"
        >
          <v-card class="d-flex flex-column m-0">
            <v-card-title class="pa-0">
              <v-toolbar
                color="primary"
                dark
                flat
              >
                <v-toolbar-title class="dialog-title text-white">
                  {{ $t('content-creator.send-email.title') }}
                </v-toolbar-title>
                <v-spacer />
                <v-toolbar-items>
                  <v-btn
                    icon
                    @click="closeDialog"
                  >
                    <v-icon
                      size="large"
                      class="text-white"
                    >
                      mdi-close
                    </v-icon>
                  </v-btn>
                </v-toolbar-items>
              </v-toolbar>
            </v-card-title>
            <v-card-text>
              <BulkInvitation
                @updateFetchIdsParameters="updateFetchIdsParameters"
                newsletter-form
                :has-crm="isCrmActive"
                :crm-name="readableCrmName"
                :has-applicable-leads="true"
              >
                <template #selectAll>
                  <v-card
                    border
                    style="border-radius: 10px !important"
                    class="mb-4"
                  >
                    <v-card-text class="pa-5">
                      <v-skeleton-loader
                        :loading="acquisitionBoosterLeadIdsLoading"
                        height="20"
                        type="text"
                      >
                        <div>
                          <span
                            v-html="
                              $t('lead-list.some-contacts-selected', {
                                amount: selectedItemsLength,
                              })
                            "
                          />
                        </div>
                      </v-skeleton-loader>
                    </v-card-text>
                  </v-card>
                </template>
              </BulkInvitation>

              <SendPreview
                :can-send-preview="canSendPreview"
                :email="$auth.user.email"
                :is-sending-preview="isSendingPreview"
                :successfully-sent-preview="successfullySentPreview"
                @send-preview="sendPreviewEmail"
                @show-success="successfullySentPreview = true"
                @dismiss-success="successfullySentPreview = false"
              />
              <v-card-actions>
                <v-btn
                  color="primary"
                  elevation="0"
                  variant="text"
                  class="mt-2"
                  @click="closeDialog"
                >
                  {{ $t('labels.newsletter-back') }}
                </v-btn>
                <v-spacer />
                <v-btn
                  :disabled="selectedItemsLength === 0"
                  class="mt-2"
                  color="primary"
                  elevation="0"
                  variant="text"
                  @click="handleProceedFromContactSelection"
                >
                  <span v-if="isMasterView">{{ $t('labels.next') }}</span>
                  <span v-else>{{ $t('labels.newsletter-proceed') }}</span>
                </v-btn>
              </v-card-actions>
            </v-card-text>
          </v-card>
        </v-stepper-window-item>

        <v-stepper-window-item
          :value="1"
          class="py-0"
        >
          <v-card class="d-flex flex-column">
            <v-card-title class="pa-0">
              <v-toolbar
                color="primary"
                dark
                flat
              >
                <v-toolbar-title class="dialog-title text-white">
                  {{ $t('content-creator.send-email.stepper.title-two') }}
                </v-toolbar-title>
                <v-spacer />
                <v-toolbar-items>
                  <v-btn
                    icon
                    @click="closeDialog"
                  >
                    <v-icon
                      size="large"
                      class="text-white"
                    >
                      mdi-close
                    </v-icon>
                  </v-btn>
                </v-toolbar-items>
              </v-toolbar>
            </v-card-title>
            <v-card-text>
              <ScheduleStep
                :content="localContent"
                newsletter-form
                :is-update-for-scheduled-post="isScheduledPostPreselected"
                :is-scheduled-post-preselected="isUpdateForScheduledPost"
                @close="closeDialog"
                @back="step = 0"
                @updateNewsletterForm="onUpdateNewsletterForm"
                @isScheduledPost="isScheduledPost = $event"
              />
            </v-card-text>
          </v-card>
        </v-stepper-window-item>
        <!-- Newsletter details + preview (Step 1): master + single-tenant -->
        <v-stepper-window-item
          :value="2"
          class="py-0"
        >
          <v-card class="d-flex flex-column">
            <v-card-title class="pa-0">
              <v-toolbar
                color="primary"
                dark
                flat
              >
                <v-toolbar-title class="dialog-title text-white">
                  {{ $t('content-creator.newsletter-details.title') }}
                </v-toolbar-title>
                <v-spacer />
                <v-toolbar-items>
                  <v-btn
                    icon
                    @click="closeDialog"
                  >
                    <v-icon
                      size="large"
                      class="text-white"
                    >
                      mdi-close
                    </v-icon>
                  </v-btn>
                </v-toolbar-items>
              </v-toolbar>
            </v-card-title>
            <div class="newsletter-preview-step-wrapper">
              <PreviewStep
                v-model:page="previewPage"
                :content="newsletterPreviewContent"
                :offices="officePreviewData"
                :is-posting="isInviting"
                :items-length="!isMasterView ? leadsPreviewTotal : undefined"
                :is-loading-offices="!isMasterView ? leadsPreviewLoading : false"
                :items-per-page="previewItemsPerPage"
                :recipient-only-table="!isMasterView"
                :show-post-column="false"
                :submit-text="$t('labels.newsletter-proceed')"
                :show-back-button="true"
                :hide-submit-button="false"
                :custom-statistics="newsletterStatistics"
                :preview-title="$t('content-creator.newsletter-details.email-preview')"
                :table-title="$t('content-creator.newsletter-details.preview-table-title')"
                :second-column-title="
                  isMasterView
                    ? $t('content-creator.newsletter-details.table.recipients')
                    : $t('content-creator.newsletter-details.table.recipient')
                "
                :is-newsletter-mode="true"
                @back="step = 1"
                @load-items="handlePreviewTableLoadItems"
                @submit="handleSuccess"
              >
                <template #preview>
                  <div class="newsletter-preview-wrapper">
                    <div
                      v-if="emailSubject"
                      class="newsletter-subject-line"
                    >
                      {{ emailSubject }}
                    </div>
                    <div
                      class="newsletter-email-content"
                      v-html="formatNewsletterHtml(emailPreviewHtmlForMaster)"
                    />
                  </div>
                </template>
              </PreviewStep>
            </div>
          </v-card>
        </v-stepper-window-item>
        <v-stepper-window-item
          :value="3"
          class="py-0"
        >
          <v-card class="d-flex flex-column">
            <v-card-title class="pa-0">
              <v-toolbar
                color="primary"
                dark
                flat
              >
                <v-toolbar-title class="dialog-title text-white">
                  <span v-if="actionType !== 'reschedule'">
                    <span v-if="!isScheduledPost">{{
                      $t('content-creator.send-email.stepper.title-four')
                    }}</span>
                    <span v-else>{{ $t('content-creator.send-email.stepper.title-nine') }}</span>
                  </span>
                  <span v-else>{{ $t('content-creator.send-email.stepper.title-seven') }}</span>
                </v-toolbar-title>
                <v-spacer />
                <v-toolbar-items>
                  <v-btn
                    icon
                    @click="closeDialog"
                  >
                    <v-icon
                      size="large"
                      class="text-white"
                    >
                      mdi-close
                    </v-icon>
                  </v-btn>
                </v-toolbar-items>
              </v-toolbar>
            </v-card-title>
            <v-card-text>
              <div class="d-flex flex-column align-center justify-center mb-4">
                <GenericAnimation
                  :animation-data="animationData"
                  loop
                  autoplay
                  :width="100"
                  :speed="0.5"
                />
                <span v-if="actionType === 'reschedule' || isScheduledPost">
                  {{ t('alerts.acquisition-booster.invitation-button.schedule-invitation') }}
                </span>
                <span v-else>
                  {{ t('alerts.acquisition-booster.invitation-button.newsletter-send-invitation') }}
                </span>
              </div>
            </v-card-text>
          </v-card>
        </v-stepper-window-item>
        <v-stepper-window-item
          :value="4"
          class="py-0"
        >
          <v-card class="d-flex flex-column">
            <v-card-title class="pa-0">
              <v-toolbar
                color="primary"
                dark
                flat
              >
                <v-toolbar-title class="dialog-title text-white">
                  <span v-if="actionType !== 'reschedule'">
                    <span v-if="!isScheduledPost">
                      {{ $t('content-creator.send-email.stepper.title-five') }}
                    </span>
                    <span v-else>{{ $t('content-creator.send-email.stepper.title-eight') }}</span>
                  </span>

                  <span v-else>{{ $t('content-creator.send-email.stepper.title-six') }}</span>
                </v-toolbar-title>
                <v-spacer />
                <v-toolbar-items>
                  <v-btn
                    icon
                    @click="closeDialog"
                  >
                    <v-icon
                      size="large"
                      class="text-white"
                    >
                      mdi-close
                    </v-icon>
                  </v-btn>
                </v-toolbar-items>
              </v-toolbar>
            </v-card-title>
            <v-card-text class="mt-4">
              <SuccessStep
                newsletter-form
                :is-reschedule="actionType === 'reschedule'"
                :is-scheduled-post="isScheduledPost"
                :newsletter-statistics="{
                  recipientSegment: getRecipientSegmentLabel,
                  totalOffices: officesCount,
                  totalRecipients: selectedItemsLength,
                  invitedAt: scheduledPosts?.invitedAt,
                }"
                :is-master-view="isMasterView"
                :newsletter-delivery-details="newsletterDeliveryDetails"
                :posts="[
                  {
                    scheduledAt: scheduledPosts?.invitedAt,
                    platforms: ['NEWSLETTER'],
                    hasFailed: false,
                    type: scheduledPosts?.textKind.toUpperCase(),
                  },
                ]"
                @close="closeDialog"
              />
            </v-card-text>
          </v-card>
        </v-stepper-window-item>
      </v-stepper-window>
    </v-stepper>
  </v-dialog>
</template>
<script setup lang="ts">
// Imports
import * as Sentry from '@sentry/vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { computed, getCurrentInstance, inject, onUnmounted, PropType, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import AnimationData from '@/assets/animations/email-plane.json'
import { AuthInstance } from '@/auth'
import GenericAnimation from '@/components/GenericAnimation.vue'
import { useBranding } from '@/composables/useBranding'
import { useSocialMedia } from '@/composables/useSocialMedia.ts'
import { CrmCompanyConfig } from '@/generated/graphql'
import { PROVIDERS } from '@/lib/crm'
import { formatDate, getTime } from '@/lib/dateTime'
import { isGatewayTimeoutError } from '@/lib/isGatewayTimeoutError'
import setQueryParameters from '@/lib/setQueryParameters'
import { showSnackbarMessage } from '@/lib/snackbarMessages'
import { htmlReplace } from '@/lib/truncate'
import GET_CHILD_COMPANIES from '@/modules/company/queries/ChildCompanies.gql'
import SendPreview from '@/modules/contentCreator/creator/newsletter/SendPreview.vue'
import DELETE_SAVED_CONTENT_FOR_NEWSLETTER from '@/modules/contentCreator/creator/queries/deleteSavedContentForNewsletter.gql'
import GET_ACTIVE_TOPICS from '@/modules/contentCreator/creator/queries/getActiveTopics.gql'
import GET_COMPANIES_POSTING_INFO from '@/modules/contentCreator/creator/queries/getCompaniesPostingInfo.gql'
import GET_NEWSLETTER_DELIVERY_DETAILS from '@/modules/contentCreator/creator/queries/getNewsletterDeliveryDetails.gql'
import GET_NEWSLETTER_OFFICE_DISTRIBUTION from '@/modules/contentCreator/creator/queries/getNewsletterOfficeDistribution.gql'
import SAVE_CONTENT from '@/modules/contentCreator/creator/queries/saveContent.gql'
import SEND_NEWSLETTER_PREVIEW from '@/modules/contentCreator/creator/queries/sendNewsletterPreview.gql'
import PreviewStep from '@/modules/contentCreator/creator/SocialMedia/PreviewStep.vue'
import ScheduleStep from '@/modules/contentCreator/creator/SocialMedia/ScheduleStep.vue'
import SuccessStep from '@/modules/contentCreator/creator/SocialMedia/SuccessStep.vue'
import {
  InvitationContactFilter,
  resolveRecipientSegmentLabel,
} from '@/modules/leads/enums/Invitation.js'
import { NEW_CONTACT_STATUSES } from '@/modules/leads/enums/Status'
import BulkInvitation from '@/modules/leads/LeadList/BulkInvitation.vue'
import ACQUISITION_BOOSTER_LEAD_IDS from '@/modules/leads/queries/AcquisitionBoosterLeadIds.gql'
import INVITE_LEADS_TO_LANDINGPAGE from '@/modules/leads/queries/InviteLeadsToLandingpage.gql'
import LEADS from '@/modules/leads/queries/Leads.gql'
import UPDATE_INVITE_LEADS_TO_LANDINGPAGE from '@/modules/leads/queries/UpdateInviteLeadsToLandingpage.gql'
import COMPANY from '@/modules/settings/companySettings/queries/CompanyData.gql'
// Props
const props = defineProps({
  content: Object as PropType<Record<string, any>>,
  disabled: Boolean,
  scheduledEmailsDeleted: Boolean,
  company: { type: Object, default: () => ({}) },
  useReschedule: { type: Boolean, default: false },
  isMasterView: { type: Boolean, default: false },
  canSendPreview: { type: Boolean, default: false },
  selectedOffices: { type: Array, default: () => [] },
  publication: { type: String, default: '' },
  includeHeadOffice: { type: Boolean, default: false },
  targetLink: { type: String, default: '' },
  individualLink: { type: String, default: '' },
  returnCallLandingpageUrl: { type: String, default: '' },
})

// Setup
const { t } = useI18n()
const instance = getCurrentInstance()
const { primaryColor } = useBranding()
const { appContext } = instance!
const $auth = inject<AuthInstance>('auth')
const $tracking = appContext.config.globalProperties.$tracking
const emit = defineEmits(['contentSaved', 'savedScheduledContent'])
const crmConfigData = inject<{ value: Omit<CrmCompanyConfig, '__typename'> }>('crmConfigData')
const isCrmActive = computed(() => crmConfigData?.value?.isActive)
const readableCrmName = computed(() => {
  const provider = crmConfigData?.value?.crmProvider
  return PROVIDERS.find(({ value }) => value === provider)?.text || 'Ihrem CRM'
})

const { contentCreatorFeature } = useSocialMedia()
const excludedCompanyIds = computed<string[]>(
  () => contentCreatorFeature.value?.config?.excludedCompanyIdsFromPosting || []
)

type Filters = {
  newsletter: boolean
  propertyRequests?: boolean
  valuations?: boolean
  currentStatus?: string[]
  leadOriginCrm?: boolean
  slug?: string
  returnCalls?: boolean
  noGuideChecklistDownload?: boolean
  companyIds?: string[]
}

// Refs and state
const dialog = ref(false)
const step = ref(0)
const isScheduledPostPreselected = ref(false)
const isUpdateForScheduledPost = ref(false)
const animationData = ref(AnimationData)
const isScheduledPost = ref(false)
const selectedLeadIds = ref([])
const filters = ref<Filters>({ newsletter: true })
const selectedContactFilter = ref<string>(InvitationContactFilter.ALL)
const isInviting = ref(false)
const scheduledPosts = ref(null)
const previewHtml = ref('')
const newsletterScheduledAt = ref(null)
const savedContentId = ref(null)
const isTrackingDelivery = ref(false)
const scheduledEmailsDeletedLocal = ref(props.scheduledEmailsDeleted)
const actionType = ref('')
const localContent = ref({ ...props.content })
const isSendingPreview = ref(false)
const successfullySentPreview = ref(false)
const previewPage = ref(1)
const previewItemsPerPage = ref(10)

onUnmounted(() => {
  successfullySentPreview.value = false
  isTrackingDelivery.value = false
})

// Queries
const { result: getCompany } = useQuery(COMPANY, {
  id: $auth.user.companyId,
})

const { result: activeTopics } = useQuery(GET_ACTIVE_TOPICS)

// Live delivery status for the newsletter just sent, polled until nothing is
// pending anymore, so the success screen doesn't have to wait for the whole
// (potentially long-running) bulk send to finish.
const { result: deliveryDetailsResult, stop: stopDeliveryDetailsPolling } = useQuery(
  GET_NEWSLETTER_DELIVERY_DETAILS,
  () => ({ input: { savedContentId: savedContentId.value, isMasterView: false } }),
  () => ({
    enabled: isTrackingDelivery.value && !!savedContentId.value && !props.isMasterView,
    pollInterval: 4000,
    fetchPolicy: 'network-only',
  })
)

const newsletterDeliveryDetails = computed(
  () => deliveryDetailsResult.value?.getNewsletterDeliveryDetails ?? null
)

watch(newsletterDeliveryDetails, (details) => {
  // total === 0 means the backend hasn't created any Invitation records yet
  // (it now processes the batch in the background) - keep polling until
  // there's actually something to report, not just nothing pending.
  if (details && details.total > 0 && details.pending === 0) {
    isTrackingDelivery.value = false
    stopDeliveryDetailsPolling()
  }
})

// Master view queries for office distribution
const { result: childCompaniesResult } = useQuery(GET_CHILD_COMPANIES, {}, () => ({
  enabled: props.isMasterView,
}))

const { result: companiesPostingInfoResult } = useQuery(
  GET_COMPANIES_POSTING_INFO,
  () => ({
    input: {
      parentId: $auth.user.companyId || '',
      topicId: localContent.value?.topicId || '',
    },
  }),
  () => ({
    enabled: props.isMasterView && !!$auth.user.companyId && !!localContent.value?.topicId,
  })
)

const companyIds = computed(() => {
  const ids = []
  if (props.includeHeadOffice && props.publication !== 'all') {
    ids.push($auth.user.companyId)
  }
  if (props.selectedOffices.length > 0) {
    ids.push(...props.selectedOffices.map((office: any) => office.id))
  }
  if (props.publication === 'all') {
    const excludedIds = props.isMasterView
      ? new Set<string>(excludedCompanyIds.value)
      : new Set<string>()

    const childCompanyIds =
      childCompaniesResult.value?.childCompanies
        ?.filter((company) => !excludedIds.has(company.id))
        .map((company) => company.id) || []

    ids.push(...childCompanyIds)
  }
  return [...new Set(ids)]
})

const { result: newsletterOfficeDistributionResult, refetch: refetchOfficeDistribution } = useQuery(
  GET_NEWSLETTER_OFFICE_DISTRIBUTION,
  () => ({
    input: {
      companyId: $auth.user.companyId || '',
      landingpageSlug: localContent.value?.topic || '',
      filters: {
        ...filters.value,
        companyIds: companyIds.value,
        targetLink: props.targetLink,
        individualLink: props.individualLink,
      },
    },
  }),
  () => ({
    enabled:
      props.isMasterView &&
      !!$auth.user.companyId &&
      !!localContent.value?.topic &&
      !!filters.value,
  })
)

const { result: leadsPreviewResult, loading: leadsPreviewLoading } = useQuery(
  LEADS,
  () => ({
    input: {
      companyId: $auth.user.companyId,
      filters: { ...filters.value },
      landingpageSlugFilter: localContent.value?.topic || '',
      itemsPerPage: previewItemsPerPage.value,
      page: previewPage.value,
    },
    landingpageSlug: localContent.value?.topic || '',
  }),
  () => ({
    enabled:
      !props.isMasterView &&
      step.value === 2 &&
      !!$auth.user.companyId &&
      !!localContent.value?.topic,
  })
)

const company = ref(props.company)
const selectedItemsLength = computed(
  () => leadsPreviewTotal.value || selectedLeadIds.value?.length || 0
)

// Utility function to clean CTA button label from HTML tags and styles
const cleanCtaButtonLabel = (innerContent: string): string => {
  return innerContent
    .replace(/(&lt;br&gt;|<br\s*\/?>)/gi, '')
    .replace(/background-color:[^;]+;?/gi, '')
    .replace(/color:[^;]+;?/gi, '')
    .replace(/padding:[^;]+;?/gi, '')
    .replace(/width:[^;]+;?/gi, '')
    .replace(/border:[^;]+;?/gi, '')
    .replace(/text-shadow:[^;]+;?/gi, '')
    .replace(/font-weight:[^;]+;?/gi, '')
    .replace(/-webkit-font-smoothing:[^;]+;?/gi, '')
    .replace(/-moz-osx-font-smoothing:[^;]+;?/gi, '')
    .replace(/"&gt;/g, '')
    .replace(/&gt;/g, '')
    .replace(/["']/g, '')
    .replace(/^\s*>+/, '')
    .trim()
}

// Compute dialog width based on step and master view
const getDialogWidth = computed(() => {
  if (step.value === 2) {
    return '80vw'
  }
  if (!props.isMasterView && step.value === 2) {
    return 1000
  }
  return 500
})

// Helper to get the schedule step number based on view
const getLoadingStepNumber = computed(() => (props.isMasterView ? 3 : 3))
const getSuccessStepNumber = computed(() => (props.isMasterView ? 4 : 4))

// Master view newsletter details computed properties
const officesCount = computed(() => {
  if (!props.isMasterView) return 1

  // Use backend data if available
  if (newsletterOfficeDistributionResult.value?.getNewsletterOfficeDistribution) {
    return newsletterOfficeDistributionResult.value.getNewsletterOfficeDistribution.totalOffices
  }

  // Fallback: Count from selected offices based on publication type
  if (props.publication === 'all') {
    return companyIds.value.length
  } else if (props.publication === 'head') {
    return 1
  } else if (props.publication === 'individual') {
    const count = props.selectedOffices.length + (props.includeHeadOffice ? 1 : 0)
    return count
  }

  return 0
})

const getRecipientSegmentLabel = computed(() =>
  resolveRecipientSegmentLabel(selectedContactFilter.value, t, {
    crmName: readableCrmName.value,
  })
)

const leadsPreviewTotal = computed(() => leadsPreviewResult.value?.leads?.total ?? 0)

const handlePreviewTableLoadItems = ({
  page,
  itemsPerPage,
}: {
  page: number
  itemsPerPage: number
}) => {
  previewPage.value = page
  previewItemsPerPage.value = itemsPerPage
}

watch(step, (newStep, oldStep) => {
  if (newStep === 2 && oldStep === 1 && !props.isMasterView) {
    previewPage.value = 1
  }
})

const officePreviewData = computed(() => {
  if (!props.isMasterView) {
    const leads = leadsPreviewResult.value?.leads?.entries || []
    return leads.map((lead: { firstname?: string; lastname?: string; email?: string }) => ({
      name: [lead.firstname, lead.lastname].filter(Boolean).join(' '),
      socialMediaCount: lead.email || '',
      targetLink: '',
      postUrl: '',
    }))
  }

  // Use backend newsletter distribution data if available
  if (newsletterOfficeDistributionResult.value?.getNewsletterOfficeDistribution) {
    const distributionData =
      newsletterOfficeDistributionResult.value.getNewsletterOfficeDistribution
    return distributionData.offices.map((office) => ({
      name: office.name,
      socialMediaCount: office.recipientCount.toString(),
      targetLink: office.targetLink,
      website: office.website,
      postUrl: '',
    }))
  }

  // Fallback: Build office data manually from available queries
  const childCompanies = childCompaniesResult.value?.childCompanies || []
  const companiesPostingInfo = companiesPostingInfoResult.value?.getCompaniesPostingInfo || []
  // Build company name map
  const companyMap = {}
  if ($auth.user.companyId && company.value?.name) {
    companyMap[$auth.user.companyId] = company.value.name
  }
  childCompanies.forEach((comp) => {
    if (comp && comp.id) {
      companyMap[comp.id] = comp.name || comp.id
    }
  })

  // Build preview data for each office
  const mappedCompanyIds = companyIds.value
  return mappedCompanyIds.map((companyId) => {
    const companyName = companyMap[companyId] || companyId
    const postingInfo = companiesPostingInfo.find((info) => info.id === companyId)
    const targetLink = postingInfo?.landingpageUrl || ''
    const website = postingInfo?.website || ''

    return {
      name: companyName,
      socialMediaCount: '—', // Placeholder - actual count requires backend query per office
      targetLink,
      website,
      postUrl: '',
    }
  })
})

const newsletterStatistics = computed(() =>
  [
    {
      icon: 'mdi-account-group',
      text: `${t('content-creator.newsletter-details.recipient-segment')}: ${getRecipientSegmentLabel.value}`,
    },
    {
      icon: 'mdi-calendar-month-outline',
      text: isScheduledPost.value
        ? formatDate(new Date(newsletterScheduledAt.value), 'dateLong')
        : formatDate(new Date(), 'dateLong'),
    },
    {
      icon: 'mdi-calendar-clock',
      text: isScheduledPost.value
        ? getTime(new Date(newsletterScheduledAt.value))
        : t('labels.newsletter-now'),
    },
    props.isMasterView && {
      icon: 'mdi-office-building-outline',
      text: `${officesCount.value} ${t('content-creator.newsletter-details.offices')}`,
    },
    {
      icon: 'mdi-badge-account-horizontal-outline',
      text: `${selectedItemsLength.value.toLocaleString()} ${t('content-creator.newsletter-details.recipients')}`,
    },
  ].filter(Boolean)
)

const newsletterPreviewContent = computed(() => ({
  topicName:
    localContent.value.topicName ||
    activeTopics.value?.topics?.find((t) => t.id === localContent.value.topicId)?.name,
  text: '',
  image: undefined,
  subject: undefined,
  date: isScheduledPost.value ? newsletterScheduledAt.value : undefined,
}))

const emailSubject = computed(() => {
  if (localContent.value.subject) return localContent.value.subject

  const [subjectLine] = localContent.value.text?.split('\n') || []
  if (/^(?:Subject|Betreff):\s*/i.test(subjectLine)) {
    return subjectLine.replace(/^(?:Subject|Betreff):\s*/i, '').trim()
  }
  return ''
})

const emailPreviewHtmlForMaster = computed(() => {
  let html = ''

  // Parse content
  const [subjectLine, ...bodyLines] = localContent.value.text?.split('\n') || []
  let text = localContent.value.text

  if (/^(?:Subject|Betreff):\s*/i.test(subjectLine)) {
    text = bodyLines
      .join('\n')
      .trim()
      .replace(/\sstyle="[^"]*"/gi, '')
  }

  html = text?.replace(/\n/g, '<br>') || ''

  // Handle CTA button
  if (/<CtaButton\b[^>]*>/i.test(html)) {
    html = html.replace(/<CtaButton\b[^>]*>([\s\S]*?)<\/CtaButton>/gi, (match, innerContent) => {
      const label = cleanCtaButtonLabel(innerContent)

      return `<button style="
              background-color: ${primaryColor.value};
              color: #ffffff;
              padding: 10px;
              width: 200px;
              border: none;
              text-shadow: none;
              font-weight: bold;
              -webkit-font-smoothing: antialiased;
              -moz-osx-font-smoothing: grayscale;
            ">${label}</button>`
    })
  }

  // Add image if present
  if (localContent.value.image) {
    const optimizedImageUrl = setQueryParameters(localContent.value.image, { width: 700 })
    const imageHtml = `<img
      src="${optimizedImageUrl}"
      alt="${t('labels.newsletter-image-alt')}"
      style="
        max-width: 600px;
        max-height: 350px;
        width: 100%;
        height: auto;
        object-fit: contain;
        margin-bottom: 20px;
        display: block;"
      />`
    html = imageHtml + html
  }

  return html + ' <br/> <br/> ' + getMasterSignatureHtml()
})

// GraphQL mutations
const { mutate: acquisitionBoosterLeadIds, loading: acquisitionBoosterLeadIdsLoading } =
  useMutation(ACQUISITION_BOOSTER_LEAD_IDS)
const { mutate: inviteLeadsToLandingpage } = useMutation(INVITE_LEADS_TO_LANDINGPAGE)
const { mutate: updateInviteLeadsToLandingpage } = useMutation(UPDATE_INVITE_LEADS_TO_LANDINGPAGE)
const { mutate: deleteSavedContent } = useMutation(DELETE_SAVED_CONTENT_FOR_NEWSLETTER)
const { mutate: savedContent } = useMutation(SAVE_CONTENT)
const { mutate: sendNewsletterPreviewMutation } = useMutation(SEND_NEWSLETTER_PREVIEW)

// Utility functions
const closeDialog = () => {
  step.value = 0
  selectedLeadIds.value = []
  filters.value = { newsletter: true }
  selectedContactFilter.value = InvitationContactFilter.ALL
  isScheduledPost.value = false
  localContent.value = {}
  dialog.value = false
  successfullySentPreview.value = false
  previewPage.value = 1
  previewItemsPerPage.value = 25
  isTrackingDelivery.value = false
  stopDeliveryDetailsPolling()
}

const onUpdateNewsletterForm = (event) => {
  newsletterScheduledAt.value = event.scheduledAt
  step.value = 2
}

const formatNewsletterHtml = (html) => {
  if (!html) return ''
  return htmlReplace(html)
}

const getMasterSignatureHtml = () => {
  return {
    ['all']: t('content-creator.newsletter-details.signature.all'),
    ['head']:
      company.value?.ambassador?.signature &&
      company.value.ambassador.signature.customTemplateSignature
        ? company.value.ambassador.signature.html || company.value.ambassador.signature.text
        : '',
    ['individual']: t('content-creator.newsletter-details.signature.individual'),
  }[props.publication || 'all']
}

const isTheSameDateAndHour = (date1, date2) => {
  const oldDate = new Date(date1)
  const newDate = new Date(date2)

  return (
    oldDate.getFullYear() === newDate.getFullYear() &&
    oldDate.getMonth() === newDate.getMonth() &&
    oldDate.getDate() === newDate.getDate() &&
    oldDate.getHours() === newDate.getHours()
  )
}

const handleSuccess = async (post = undefined) => {
  if (isTheSameDateAndHour(localContent.value?.scheduledAt, post?.scheduledAt)) {
    const scheduledAt = post?.scheduledAt || newsletterScheduledAt.value
    showSnackbarMessage(
      'error',
      `${t('social-media.error.same-invitation-date')}: ${formatDate(scheduledAt)}`
    )
    return
  }
  const [subjectLine, ...bodyLines] = localContent.value.text.split('\n')
  if (/^(?:Subject|Betreff):\s*/i.test(subjectLine)) {
    localContent.value.subject = subjectLine.replace(/^(?:Subject|Betreff):\s*/i, '').trim()
    localContent.value.text = bodyLines
      .join('\n')
      .trim()
      .replace(/\sstyle="[^"]*"/gi, '')
  }

  previewHtml.value = localContent.value?.text?.replace(/\n/g, '<br>') || ''
  if (/<CtaButton\b[^>]*>/i.test(previewHtml.value)) {
    previewHtml.value = previewHtml.value.replace(
      /<CtaButton\b[^>]*>([\s\S]*?)<\/CtaButton>/gi,
      (match, innerContent) => {
        const label = cleanCtaButtonLabel(innerContent)

        return `<button style="
              background-color: ${primaryColor.value};
              color: #ffffff;
              padding: 10px;
              width: 200px;
              border: none;
              text-shadow: none;
              font-weight: bold;
              -webkit-font-smoothing: antialiased;
              -moz-osx-font-smoothing: grayscale;
            ">${label}</button>`
      }
    )
  }

  if (localContent.value.image) {
    const optimizedImageUrl = setQueryParameters(localContent.value.image, { width: 700 })
    const imageHtml = `<img
      src="${optimizedImageUrl}"
      alt="${t('labels.newsletter-image-alt')}"
      style="
        max-width: 600px;
        max-height: 350px;
        width: 100%;
        height: auto;
        object-fit: contain;
        margin-bottom: 20px;
        display: block;"
      />`
    previewHtml.value = imageHtml + previewHtml.value
  }

  scheduledPosts.value = {
    ...localContent.value,
    invitedAt: newsletterScheduledAt.value ?? null,
  }
  submit()
}

const handleSlotAction = async (type) => {
  actionType.value = type
  company.value = getCompany.value?.company
  if (type === 'reschedule') {
    isScheduledPostPreselected.value = true
    isUpdateForScheduledPost.value = true
    step.value = 1
  } else {
    isScheduledPostPreselected.value = false
    isUpdateForScheduledPost.value = false
  }
}

const handleProceedFromContactSelection = () => {
  step.value = 1
}

const updateInvitationParameters = async () => {
  try {
    const {
      data: { getAcquisitionBoosterLeadIds },
    } = await acquisitionBoosterLeadIds({
      input: {
        companyId: $auth.user.companyId,
        landingpageSlug: localContent.value.topic,
        filters: { ...filters.value, companyIds: companyIds.value },
        isMasterView: props.isMasterView,
      },
    })
    selectedLeadIds.value = getAcquisitionBoosterLeadIds.leadIds
  } catch (error) {
    showSnackbarMessage('error', t('alerts.acquisition-booster.newsletter-error'))
  }
}

const relevantSlugs = [
  'immobilie-geerbt',
  'immobilie-in-der-scheidung',
  'privater-immobilienverkauf',
  'wohnen-im-alter',
  'sanierung-einer-immobilie',
  'immobilie-in-der-scheidung',
  'immobile-divorzio',
  'vendita-privata-immobile',
  'abitare-terza-eta',
  'ristrutturazione-immobile',
  'immobilier-divorce',
  'vente-privee-bien',
  'logement-senior',
  'renovation-bien',
]
const updateFetchIdsParameters = async ({ contactFilter }) => {
  if (contactFilter) {
    selectedContactFilter.value = contactFilter
  }
  filters.value = { newsletter: true }
  if (contactFilter === InvitationContactFilter.PROPERTY_REQUEST)
    filters.value = { ...filters.value, propertyRequests: true }
  else if (contactFilter === InvitationContactFilter.VALUATIONS)
    filters.value = { ...filters.value, valuations: true }
  else if (contactFilter === InvitationContactFilter.WITHOUT_VALUATIONS)
    filters.value = { ...filters.value, valuations: false }
  else if (contactFilter === InvitationContactFilter.NEW)
    filters.value = { ...filters.value, currentStatus: NEW_CONTACT_STATUSES }
  else if (contactFilter === InvitationContactFilter.CRM)
    filters.value = { ...filters.value, leadOriginCrm: true }
  else if (relevantSlugs.includes(contactFilter))
    filters.value = { ...filters.value, slug: contactFilter }
  else if (contactFilter === InvitationContactFilter.RETURN_CALLS)
    filters.value = { ...filters.value, returnCalls: true }
  else if (contactFilter === InvitationContactFilter.WITHOUT_DOWNLOADS)
    filters.value = { ...filters.value, noGuideChecklistDownload: true }

  await updateInvitationParameters()
}

const storeSavedContent = async () => {
  try {
    const saveContent = await savedContent({
      input: {
        text: localContent.value.text
          ? 'Subject:' + localContent.value.subject + '\n ' + localContent.value.text
          : null,
        imagePath: localContent.value?.image ?? null,
        imageSource: localContent.value?.imageSource ?? null,
        textKind: localContent.value?.textKind,
        topicId: localContent.value?.topicId,
        downloaded: false,
        recipientSegment: selectedContactFilter.value,
        targetLink: props.targetLink,
        publication: props.publication,
        topicName: newsletterPreviewContent.value?.topicName,
        ...(isScheduledPost.value && { scheduledAt: scheduledPosts.value?.invitedAt }),
      },
    })
    $tracking.event('Content Creator', 'Created', 'Saved Saved Content')
    return saveContent
  } catch (error) {
    showSnackbarMessage('error', t('alerts.content-creator.save-content.newsletter-error'))
  }
}

const deleteStoredContent = async () => {
  try {
    await deleteSavedContent({ input: { id: savedContentId.value } })
    $tracking.event('Content Creator', 'Edited', 'Deleted Saved Content')
  } catch (error) {
    showSnackbarMessage('error', t('alerts.acquisition-booster.delete-saved-content.error'))
  }
}

// GraphQL error codes the backend only ever throws before persisting
// anything (validation/authorization/all-leads-blocked, checked up front).
// Any other code - including missing/unknown/internal-error codes - does not
// guarantee nothing was persisted, so must not trigger a rollback.
const DEFINITIVE_PRE_PERSISTENCE_ERROR_CODES = [
  'INVITATION_VALIDATION_ERROR',
  'FORBIDDEN',
  'ALL_LEADS_BLOCKED',
]

const inviteLeads = async () => {
  step.value = getLoadingStepNumber.value
  try {
    const {
      data: { saveContent },
    } = await storeSavedContent()
    if (saveContent) savedContentId.value = saveContent.id

    let invitationBodyHtml = localContent?.value?.text
      ? localContent.value.text.replace(/\n/g, '<br/>')
      : ''

    if (localContent.value.image) {
      const optimizedImageUrl = setQueryParameters(localContent.value.image, { width: 700 })
      const imageHtml = `<mj-image src="${optimizedImageUrl}" alt="${t('labels.newsletter-image-alt')}" padding="0 0 20px 0" align="center" />`
      invitationBodyHtml = imageHtml + invitationBodyHtml
    }

    await inviteLeadsToLandingpage({
      input: {
        leadIds: selectedLeadIds.value,
        landingpageSlug: localContent.value?.topic,
        invitedAt: isScheduledPost.value
          ? scheduledPosts.value?.invitedAt
          : new Date().toISOString(),
        invitationBody: invitationBodyHtml || '',
        invitationSubject: scheduledPosts.value?.subject ?? '',
        savedContentId: savedContentId.value,
        targetLink: props.targetLink,
        individualLink: props.individualLink,
        isMasterView: props.isMasterView,
        publication: props.publication,
      },
    })

    emit(isScheduledPost.value ? 'savedScheduledContent' : 'contentSaved')
    if (!isScheduledPost.value && !props.isMasterView) isTrackingDelivery.value = true
    setTimeout(() => (step.value = getSuccessStepNumber.value), 3000)
  } catch (error) {
    if (isGatewayTimeoutError(error)) {
      // Scalingo aborted the request after 30s, but the emails are still being
      // sent server-side. Present this as success and keep the saved content.
      emit(isScheduledPost.value ? 'savedScheduledContent' : 'contentSaved')
      if (!isScheduledPost.value && !props.isMasterView) isTrackingDelivery.value = true
      step.value = getSuccessStepNumber.value
      showSnackbarMessage('success', t('alerts.acquisition-booster.newsletter-sending-in-progress'))
      return
    }
    const graphQlError = error?.graphQLErrors?.[0]
    const code = graphQlError?.extensions?.code
    if (graphQlError) {
      const translatedError =
        {
          INVITATION_VALIDATION_ERROR: t(
            'alerts.acquisition-booster.errors.newsletter.invalid-landingpage-slug'
          ),
          FORBIDDEN: t('alerts.acquisition-booster.errors.newsletter.feature-disabled'),
          ALL_LEADS_BLOCKED:
            t('alerts.newsletter.error') +
            ': ' +
            graphQlError?.extensions?.blockedLeads
              ?.map(({ reason }) =>
                t(
                  `alerts.acquisition-booster.errors.newsletter.${
                    {
                      CONVERSION: 'conversion',
                      ALREADY_INVITED: 'already-invited',
                      INVITATION_QUOTA: 'invitation-quota',
                      INVITATION_LIMIT: 'invitation-limit',
                      NO_FURTHER_CONTACT: 'no-further-contact',
                      NO_DATA_PROCESSING_PERMISSION: 'no-data-processing-permission',
                      UNSUBSCRIBED: 'unsubscribed',
                    }[reason]
                  }`
                )
              )
              .join(', '),
        }[code] || t('alerts.acquisition-booster.errors.newsletter.general')
      Sentry.captureException(error, {
        contexts: {
          message: translatedError || 'Something went wrong',
          error: code,
        },
      })
      showSnackbarMessage('error', translatedError)
      // Only roll back for known, definitively pre-persistence rejections.
      // An unknown/internal error code doesn't guarantee nothing was
      // persisted (e.g. a bulk send that partially succeeded), so leave the
      // saved content in place rather than risk deleting something in use.
      if (savedContentId.value && DEFINITIVE_PRE_PERSISTENCE_ERROR_CODES.includes(code)) {
        await deleteStoredContent()
      }
    } else {
      // Ambiguous network-level error (not a clean 504, no GraphQL error) -
      // the backend may still be processing (or have completed) the send
      // using this savedContentId, so don't delete it out from under an
      // in-flight request.
      showSnackbarMessage('error', t('alerts.acquisition-booster.newsletter-error'))
    }
  } finally {
    $tracking.event('Content Creator', 'Created', 'Leads email newsletter invited')
    isInviting.value = false
    setTimeout(() => closeDialog(), 9000)
  }
}

const updateInviteLeads = async () => {
  step.value = getLoadingStepNumber.value
  try {
    await updateInviteLeadsToLandingpage({
      input: {
        invitedAt: scheduledPosts.value?.invitedAt,
        savedContentId: scheduledPosts.value?.id,
      },
    })
    emit('savedScheduledContent')
    setTimeout(() => (step.value = getSuccessStepNumber.value), 3000)
  } catch (error) {
    showSnackbarMessage('error', t('alerts.acquisition-booster.error'))
  } finally {
    $tracking.event('Content Creator', 'Edited', 'Update leads email newsletter invited')
    isInviting.value = false
    setTimeout(() => closeDialog(), 9000)
  }
}

const submit = async () => {
  isInviting.value = true
  if (actionType.value === 'reschedule') await updateInviteLeads()
  else await inviteLeads()
}

const sendPreviewEmail = async () => {
  isSendingPreview.value = true
  successfullySentPreview.value = false
  try {
    // sendPreviewEmail can run before handleSuccess() has had a chance to
    // normalize localContent (the "send preview" button is reachable from
    // the first dialog step, before content generation confirms it) - so an
    // inline "Betreff:"/"Subject:" header may still be the first line of
    // text. Extract it into subject the same way handleSuccess/emailSubject
    // do, without mutating localContent (this is a preview, not a confirm).
    let subject = localContent.value.subject
    let text = localContent.value.text

    if (!subject) {
      const [subjectLine, ...bodyLines] = text?.split('\n') || []
      if (/^(?:Subject|Betreff):\s*/i.test(subjectLine)) {
        subject = subjectLine.replace(/^(?:Subject|Betreff):\s*/i, '').trim()
        text = bodyLines
          .join('\n')
          .trim()
          .replace(/\sstyle="[^"]*"/gi, '')
      }
    }

    // subject is intentionally allowed to stay empty here: sendNewsletterPreview's
    // subject input is nullable, same as inviteLeadsToLandingpage's
    // invitationSubject for the real send - content with no subject field and
    // no parseable header sends through exactly like a real newsletter would.

    // Mirrors inviteLeads()'s body construction exactly otherwise (raw text
    // + mj-image, no CTA-button/link resolution or signature append done
    // client-side) so the preview email a maker sends themselves matches
    // what leads actually receive. The CTA-button-to-mj-button conversion,
    // link resolution and ambassador signature are all applied server-side,
    // identically for both the preview and the real send - see
    // sendNewsletterPreview/processNewsletterInvitationBatch in
    // bottimmo-service.
    let invitationBodyHtml = text ? text.replace(/\n/g, '<br/>') : ''

    if (localContent.value.image) {
      const optimizedImageUrl = setQueryParameters(localContent.value.image, { width: 700 })
      const imageHtml = `<mj-image src="${optimizedImageUrl}" alt="${t('labels.newsletter-image-alt')}" padding="0 0 20px 0" align="center" />`
      invitationBodyHtml = imageHtml + invitationBodyHtml
    }

    await sendNewsletterPreviewMutation({
      input: {
        landingpageSlug: localContent.value?.topic,
        subject,
        body: invitationBodyHtml,
        targetLink: props.targetLink,
        individualLink: props.individualLink,
      },
    })

    successfullySentPreview.value = true
    showSnackbarMessage(
      'success',
      t('alerts.acquisition-booster.invitation-preview.newsletter-success', {
        email: $auth.user.email,
      })
    )
    $tracking.event('Content Creator', 'Clicked', 'Send Newsletter Preview')
  } catch (error) {
    showSnackbarMessage(
      'error',
      t('alerts.acquisition-booster.invitation-preview.newsletter-error')
    )
  } finally {
    isSendingPreview.value = false
  }
}

// Watchers
watch(
  () => props.scheduledEmailsDeleted,
  (val) => (scheduledEmailsDeletedLocal.value = val)
)

watch(
  () => scheduledEmailsDeletedLocal.value,
  (val) => {
    if (val) {
      showSnackbarMessage('success', t('alerts.acquisition-booster.email-deleted.success'))
      scheduledEmailsDeletedLocal.value = false
    }
  },
  { immediate: true, deep: true }
)

watch(
  () => props.content,
  (val) => {
    if (val) {
      localContent.value = { ...val }
    }
  },
  { immediate: true, deep: true }
)

watch(
  () => props.useReschedule,
  () => (step.value = 2)
)
watch(
  () => dialog.value,
  (val, oldVal) => {
    if (val && !oldVal) {
      let slug = null
      if (!localContent.value?.topic) {
        slug = activeTopics.value?.topics?.find(({ id }) => id === localContent.value?.topicId)
          ?.landingpage?.slug
      }
      localContent.value = { ...props.content, topic: slug ?? props.content.topic }
    }
  }
)

watch(
  isScheduledPost,
  (val) => {
    if (val) isScheduledPost.value = val
  },
  { immediate: true }
)

// Refetch office distribution when filters or dialog state changes
watch(
  [() => filters.value, () => dialog.value, () => localContent.value?.topic],
  ([newFilters, newDialog, newTopic], [oldFilters, oldTopic]) => {
    // Only refetch if:
    // 1. Master view is enabled
    // 2. Dialog is open
    // 3. Filters have changed OR topic has changed
    if (
      props.isMasterView &&
      newDialog &&
      (JSON.stringify(newFilters) !== JSON.stringify(oldFilters) || newTopic !== oldTopic)
    ) {
      if (refetchOfficeDistribution && $auth.user.companyId && newTopic) {
        refetchOfficeDistribution()
      }
    }
  },
  { deep: true }
)
</script>
<style scoped>
.preview-title {
  z-index: 999;
}

.preview-container {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 20px 0;
}

.email-preview-wrapper {
  max-width: 700px;
  width: 100%;
}

.email-newsletter {
  border: 3px solid v-bind(primaryColor);
  max-height: 800px;
  overflow-y: scroll;
  width: 100%;
  min-height: 400px;
}

.email-newsletter::-webkit-scrollbar {
  width: 12px;
  -webkit-appearance: none;
  display: block;
}

.email-newsletter::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.email-newsletter::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 6px;
  border: 3px solid #f1f1f1;
}

.email-newsletter::-webkit-scrollbar-thumb:hover {
  background: #555;
}

::v-deep(.v-textarea .v-field) {
  background: transparent !important;
}

::v-deep(.v-field--variant-filled .v-field__overlay) {
  background-color: transparent !important;
  opacity: 1 !important;
  transition: none !important;
}

.v-dialog > .v-overlay__content > .v-sheet {
  border-radius: 16px;
}

.subject-line {
  margin: 0 0 20px 0;
  font-weight: bold;
  font-size: 1.125rem;
  padding-bottom: 10px;
  border-bottom: 1px solid #e0e0e0;
}

/* Newsletter preview step wrapper - matches SavedContentsTab pattern */
.newsletter-preview-step-wrapper {
  margin: 1.5rem;
  margin-bottom: 0;
  overflow-x: hidden;
  min-width: 0;
  width: calc(100% - 3rem);
  max-width: calc(100% - 3rem);
}

:deep(.newsletter-preview-step-wrapper .preview-step) {
  height: 100%;
  min-width: 0;
  width: 100%;
  max-width: 100%;
}

:deep(.newsletter-preview-step-wrapper .preview-step-content) {
  min-width: 0;
  overflow-x: hidden;
  width: 100%;
  max-width: 100%;
}

:deep(.newsletter-preview-step-wrapper .preview-step-content__summary) {
  min-width: 0;
  overflow-x: hidden;
}

:deep(.newsletter-preview-step-wrapper .preview-step__preview) {
  min-width: 0;
  overflow-x: hidden;
  width: 100%;
  max-width: 100%;
}

/* Newsletter preview specific styles (injected into PreviewStep slot) */
.newsletter-preview-wrapper {
  overflow-y: auto;
}
:deep(.preview-step-content) {
  grid-template-columns: 1fr 1fr;
}
.newsletter-subject-line {
  margin: 0 0 20px 0;
  font-weight: bold;
  font-size: 1.125rem;
  padding-bottom: 12px;
  border-bottom: 2px solid #e0e0e0;
}

.newsletter-email-content {
  line-height: 1.6;
  color: #000000;
}

/* Match PreviewStep scrollbar styling */
.newsletter-preview-wrapper::-webkit-scrollbar {
  width: 8px;
}

.newsletter-preview-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.newsletter-preview-wrapper::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.newsletter-preview-wrapper::-webkit-scrollbar-thumb:hover {
  background: #555;
}
.send-preview-text {
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  letter-spacing: 0;
}
:deep(.v-skeleton-loader__text) {
  margin: 0;
}
</style>
